import { useTranslation } from 'react-i18next';

/**
 * Typing-first time input — works identically on phone and desktop.
 *
 * Why we DON'T expose a native <input type="time"> picker even as a
 * fallback:
 *   • On Android/Telegram the system locale (not the app locale) drives
 *     the picker's button labels — EN users on a Russian phone see
 *     «Удалить · Отмена · Установить».
 *   • The Material dialog on narrow phones (≤ 360 dp) overflows: the
 *     "Установить" CTA gets clipped — users can't confirm their choice.
 *   • On desktop the native control is tiny spin-buttons (12:34 ↕),
 *     which look out-of-place next to our serif inputs.
 *   • Almost everyone knows their birth time in digits — typing is
 *     faster than spinning a clock face anyway.
 *
 * So the component is a plain text input with auto-colon formatting:
 *   keystrokes "1534" → "15:34" automatically. Numeric keyboard on
 *   mobile via inputMode="numeric".
 *
 * Output value: always "HH:MM" (24-hour). Matches backend expectations.
 */

function autoFormat(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  autoFocus?: boolean;
}

export default function TimeField({
  value, onChange, placeholder,
  className = '', inputClassName, autoFocus,
}: Props) {
  const { t } = useTranslation();
  const ph = placeholder ?? t('fields.time_placeholder');

  return (
    <input
      type="text"
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(autoFormat(e.target.value))}
      placeholder={ph}
      maxLength={5}
      className={
        inputClassName ??
        `w-full rounded-lg border border-gold-500/30 bg-night-900/60
         px-3 py-2 font-serif text-base text-gold-100 outline-none
         transition placeholder:text-gold-300/35 focus:border-gold-400 ${className}`
      }
      autoFocus={autoFocus}
    />
  );
}
